import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useSelfServiceClaimCard,
  useSelfServiceCardIsClaimable,
  getSelfServiceListBearersQueryKey,
  selfServiceClaimCardBody,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { getApiErrorMessage } from '@/helpers/apiError';
import { getClaimabilityError } from '@/helpers/claimability';
import { blankToUndefined } from '@/helpers/trimData';
import { IconCheck } from '@/icons/CoreIcons';
import Button from '../core/Button';
import Input from '../core/Input';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import { InputText } from '../Inputs/InputText';
import { Spinner } from '../core/Spinner';
import { WarningBanner } from '../core/WarningBanner';

interface AddTravelCardDialogProps {
  travellerId: string;
  existingCardSerials: string[];
  onOpenChange: (open: boolean) => void;
}

const normalizeCardSerial = (serial: string) => serial.replace(/\D/g, '');

export const AddTravelCardDialog = ({
  travellerId,
  existingCardSerials,
  onOpenChange,
}: AddTravelCardDialogProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const customErrorMap = useCustomErrorMap();

  const [formattedCardSerial, setFormattedCardSerial] = useState('');

  const claimCard = useSelfServiceClaimCard();

  const existingCardSerialSet = useMemo(
    () => new Set(existingCardSerials.map(normalizeCardSerial)),
    [existingCardSerials],
  );

  const formSchema = useMemo(
    () =>
      selfServiceClaimCardBody.extend({
        card_serial: z
          .string()
          .length(16, {
            message: formatMessage({
              defaultMessage: 'Kortnumret måste vara 16 siffror',
            }),
          })
          .refine((serial) => !existingCardSerialSet.has(serial), {
            message: formatMessage({
              defaultMessage: 'Det här kortet är redan kopplat till resenären',
            }),
          }),
      }),
    [existingCardSerialSet, formatMessage],
  );

  type FormData = z.infer<typeof formSchema>;

  const formMethods = useForm<FormData>({
    defaultValues: {
      traveller_id: travellerId,
      card_serial: '',
      label: '',
    },
    resolver: zodResolver(formSchema, { errorMap: customErrorMap }),
    mode: 'onChange',
  });

  const cardSerial = formMethods.watch('card_serial');
  const cardSerialError = formMethods.formState.errors.card_serial?.message;
  const isCompleteSerial = cardSerial.length === 16;

  const {
    data: claimability,
    isLoading: isLoadingClaimability,
    isError: isClaimabilityError,
  } = useSelfServiceCardIsClaimable(
    { serial: cardSerial },
    {
      query: {
        enabled: isCompleteSerial && !cardSerialError,
      },
    },
  );

  const claimabilityError = useMemo(() => {
    if (!isCompleteSerial || cardSerialError) {
      return '';
    }
    if (isClaimabilityError) {
      return formatMessage({ defaultMessage: 'Kunde inte kontrollera kortet' });
    }
    if (claimability && !claimability.claimable) {
      return getClaimabilityError(claimability.reason, formatMessage);
    }
    return '';
  }, [
    cardSerialError,
    claimability,
    formatMessage,
    isClaimabilityError,
    isCompleteSerial,
  ]);

  const isValid =
    isCompleteSerial &&
    !cardSerialError &&
    !claimabilityError &&
    claimability?.claimable === true;

  const handleCardSerialChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;
      if (value !== '0') {
        value = value.replace(/\D/g, '');
      }
      value = value.slice(0, 16);
      const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');
      setFormattedCardSerial(formattedValue);
      formMethods.setValue('card_serial', value, { shouldValidate: true });
    },
    [formMethods],
  );

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => {
      claimCard.mutate(
        {
          data: {
            traveller_id: data.traveller_id,
            card_serial: data.card_serial,
            label: blankToUndefined(data.label),
          },
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Kortet har lagts till.',
                }),
              },
              { autoClose: 3000 },
            );
            await queryClient.invalidateQueries({
              queryKey: getSelfServiceListBearersQueryKey(),
            });
            onOpenChange(false);
          },
          onError: (err) => {
            const message = getApiErrorMessage(
              err,
              formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            );
            formMethods.setError('card_serial', { message });
            toast.error({ title: message }, { autoClose: 3000 });
          },
        },
      );
    },
    [claimCard, formatMessage, queryClient, onOpenChange, formMethods],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <WarningBanner announce={false} className="mb-4">
          <FormattedMessage defaultMessage="Kortet måste vara helt blankt/oanvänt och kommer att kopplas permanent till denna resenären i portalen. Kortet går inte att få tillbaka kontroll över. Ett bättre alternativ är för det mesta att koppla kortet privat och sen fylla i sin e-post i fältet 'E-post i appen / Mina sidor' för att få det inläst och användbart i denna portalen." />
        </WarningBanner>
        <div className="flex flex-col gap-4">
          <FormSection editMode={true}>
            <FormElement
              id="card_serial"
              label={<FormattedMessage defaultMessage="Kortnummer" />}
              input={
                <Input
                  id="card_serial"
                  type="text"
                  placeholder={formatMessage({
                    defaultMessage: '1234 1234 1234 1234',
                  })}
                  value={formattedCardSerial}
                  onChange={handleCardSerialChange}
                  error={
                    formMethods.formState.errors.card_serial?.message ||
                    claimabilityError
                  }
                  endAdornment={
                    isLoadingClaimability ? (
                      <Spinner className="size-4" />
                    ) : null
                  }
                  helperTextReserveSpace
                />
              }
            />

            <FormElement
              id="label"
              label={<FormattedMessage defaultMessage="Namn på kort" />}
              input={
                <InputText
                  name="label"
                  control={formMethods.control}
                  editMode={true}
                  placeholder={formatMessage({
                    defaultMessage: '(Valfritt)',
                  })}
                />
              }
            />
          </FormSection>
        </div>
        <div className="mt-4 flex w-full flex-row justify-end gap-2">
          <Button
            onClick={() => onOpenChange(false)}
            variant="ghost"
            type="button"
          >
            <FormattedMessage defaultMessage="Avbryt" />
          </Button>
          <Button
            startIcon={<IconCheck />}
            variant="filled"
            type="submit"
            disabled={!isValid}
            loading={claimCard.isPending}
          >
            <FormattedMessage defaultMessage="Lägg till kort" />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
